/**
 * Search Command Dialog
 *
 * A command palette (⌘K) for searching across all workspace items:
 * - Tables
 * - Linked Tables
 * - Folders
 *
 * Features:
 * - Keyboard navigation
 * - Fuzzy search
 * - Recent items section
 * - Quick actions
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@loader/ui';
import { Table2, Link2, Folder, Star, Clock, ArrowRight } from 'lucide-react';
import type { WorkspaceItemType } from '@loader/shared-types';

import { useTablesListQuery } from '@/features/tables/hooks/useTableQueries';
import { useLinkedTablesListQuery } from '@/features/linkedTables/hooks/useLinkedTableQueries';
import { useFoldersQuery } from '@/features/organization/hooks';

// ============================================================================
// TYPES
// ============================================================================

interface SearchItem {
  id: string;
  type: WorkspaceItemType;
  name: string;
  description?: string | null;
  isFavorite?: boolean;
  updatedAt?: string;
  /** Folder containing this item (null = root, undefined for folders themselves) */
  folderId?: string | null;
  /** Parent folder ID (for folders only) */
  parentFolderId?: string | null;
}

interface SearchCommandDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getItemIcon(type: WorkspaceItemType) {
  switch (type) {
    case 'table':
      return <Table2 className="size-4 text-blue-500" />;
    case 'linked_table':
      return <Link2 className="size-4 text-purple-500" />;
    case 'folder':
      return <Folder className="size-4 text-amber-500" />;
  }
}

/**
 * Get the navigation path for an item.
 * - Tables/Linked Tables: Navigate to their containing folder with preview panel open
 * - Folders: Navigate into the folder
 */
function getItemPath(item: SearchItem): string {
  switch (item.type) {
    case 'table':
    case 'linked_table': {
      // Navigate to the folder containing this item, with preview panel open
      // Uses 'type' param to match workspaceSearchSchema
      const basePath = item.folderId
        ? `/workspace/folder/${item.folderId}`
        : '/workspace';
      return `${basePath}?preview=${item.id}&type=${item.type}`;
    }
    case 'folder':
      // Navigate into the folder
      return `/workspace/folder/${item.id}`;
  }
}

function getTypeLabel(type: WorkspaceItemType): string {
  switch (type) {
    case 'table':
      return 'Table';
    case 'linked_table':
      return 'Linked Table';
    case 'folder':
      return 'Folder';
  }
}

/**
 * Simple fuzzy search - checks if search terms appear in order within the text
 */
function fuzzyMatch(text: string, search: string): boolean {
  const lowerText = text.toLowerCase();
  const lowerSearch = search.toLowerCase().trim();

  if (!lowerSearch) return true;

  // Split search into words and check if all words appear in the text
  const searchWords = lowerSearch.split(/\s+/);
  return searchWords.every((word) => lowerText.includes(word));
}

// ============================================================================
// COMPONENT
// ============================================================================

export function SearchCommandDialog({ open, onOpenChange }: SearchCommandDialogProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  // Fetch data
  const { data: tables = [] } = useTablesListQuery({ enabled: open });
  const { data: linkedTables = [] } = useLinkedTablesListQuery({ enabled: open });
  const { data: folders = [] } = useFoldersQuery({ enabled: open });

  // Reset search when dialog closes
  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  // Transform data into search items
  const allItems = useMemo((): SearchItem[] => {
    const items: SearchItem[] = [];

    // Add tables (exclude deleted)
    tables.forEach((table) => {
      if (table.deleted_at) return;
      items.push({
        id: table.id,
        type: 'table',
        name: table.name,
        description: table.description,
        isFavorite: table.is_favorite,
        updatedAt: table.updated_at,
        folderId: table.folder_id,
      });
    });

    // Add linked tables (exclude deleted)
    linkedTables.forEach((lt) => {
      if (lt.deleted_at) return;
      items.push({
        id: lt.id,
        type: 'linked_table',
        name: lt.name,
        description: lt.description,
        isFavorite: lt.is_favorite,
        updatedAt: lt.updated_at,
        folderId: lt.folder_id,
      });
    });

    // Add folders (exclude deleted)
    folders.forEach((folder) => {
      if (folder.deleted_at) return;
      items.push({
        id: folder.id,
        type: 'folder',
        name: folder.name,
        updatedAt: folder.updated_at,
        parentFolderId: folder.parent_folder_id,
      });
    });

    return items;
  }, [tables, linkedTables, folders]);

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!search.trim()) {
      return allItems;
    }

    return allItems.filter((item) => {
      // Search in name and description
      const nameMatch = fuzzyMatch(item.name, search);
      const descMatch = item.description ? fuzzyMatch(item.description, search) : false;
      return nameMatch || descMatch;
    });
  }, [allItems, search]);

  // Get recent items (last 7 days, sorted by updated_at)
  const recentItems = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);

    return allItems
      .filter((item) => {
        if (!item.updatedAt) return false;
        return new Date(item.updatedAt) >= cutoff;
      })
      .sort((a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateB - dateA;
      })
      .slice(0, 5);
  }, [allItems]);

  // Get favorite items
  const favoriteItems = useMemo(() => {
    return allItems.filter((item) => item.isFavorite).slice(0, 5);
  }, [allItems]);

  // Handle item selection
  const handleSelect = useCallback(
    (item: SearchItem) => {
      onOpenChange(false);
      navigate({ to: getItemPath(item) });
    },
    [navigate, onOpenChange]
  );

  // Handle quick navigation
  const handleQuickNav = useCallback(
    (path: string) => {
      onOpenChange(false);
      navigate({ to: path });
    },
    [navigate, onOpenChange]
  );

  const hasSearch = search.trim().length > 0;
  const noResults = hasSearch && filteredItems.length === 0;

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search"
      description="Search for tables, linked tables, and folders"
    >
      <CommandInput
        placeholder="Search tables, linked tables, folders..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>
          <div className="flex flex-col items-center gap-2 py-6">
            <p className="text-muted-foreground">No results found for &quot;{search}&quot;</p>
            <p className="text-muted-foreground text-xs">Try a different search term</p>
          </div>
        </CommandEmpty>

        {/* Search Results */}
        {hasSearch && filteredItems.length > 0 && (
          <CommandGroup heading="Results">
            {filteredItems.slice(0, 10).map((item) => (
              <CommandItem
                key={`${item.type}-${item.id}`}
                value={`${item.type}-${item.id}-${item.name}`}
                onSelect={() => handleSelect(item)}
                className="flex items-center gap-3"
              >
                {getItemIcon(item.type)}
                <div className="flex flex-1 flex-col gap-0.5 overflow-hidden">
                  <span className="truncate font-medium">{item.name}</span>
                  {item.description && (
                    <span className="text-muted-foreground truncate text-xs">
                      {item.description}
                    </span>
                  )}
                </div>
                <span className="text-muted-foreground text-xs">{getTypeLabel(item.type)}</span>
              </CommandItem>
            ))}
            {filteredItems.length > 10 && (
              <div className="text-muted-foreground px-2 py-1.5 text-center text-xs">
                +{filteredItems.length - 10} more results
              </div>
            )}
          </CommandGroup>
        )}

        {/* Default view when no search */}
        {!hasSearch && !noResults && (
          <>
            {/* Quick Actions */}
            <CommandGroup heading="Quick Actions">
              <CommandItem
                onSelect={() => handleQuickNav('/workspace')}
                className="flex items-center gap-3"
              >
                <ArrowRight className="text-muted-foreground size-4" />
                <span>Go to All Items</span>
              </CommandItem>
              <CommandItem
                onSelect={() => handleQuickNav('/workspace/favorites')}
                className="flex items-center gap-3"
              >
                <Star className="text-muted-foreground size-4" />
                <span>Go to Favorites</span>
              </CommandItem>
              <CommandItem
                onSelect={() => handleQuickNav('/workspace/recent')}
                className="flex items-center gap-3"
              >
                <Clock className="text-muted-foreground size-4" />
                <span>Go to Recent</span>
              </CommandItem>
            </CommandGroup>

            {/* Favorites */}
            {favoriteItems.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Favorites">
                  {favoriteItems.map((item) => (
                    <CommandItem
                      key={`fav-${item.type}-${item.id}`}
                      value={`fav-${item.type}-${item.id}-${item.name}`}
                      onSelect={() => handleSelect(item)}
                      className="flex items-center gap-3"
                    >
                      {getItemIcon(item.type)}
                      <div className="flex flex-1 flex-col gap-0.5 overflow-hidden">
                        <span className="truncate">{item.name}</span>
                      </div>
                      <Star className="size-3 fill-amber-400 text-amber-400" />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {/* Recent */}
            {recentItems.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Recent">
                  {recentItems.map((item) => (
                    <CommandItem
                      key={`recent-${item.type}-${item.id}`}
                      value={`recent-${item.type}-${item.id}-${item.name}`}
                      onSelect={() => handleSelect(item)}
                      className="flex items-center gap-3"
                    >
                      {getItemIcon(item.type)}
                      <div className="flex flex-1 flex-col gap-0.5 overflow-hidden">
                        <span className="truncate">{item.name}</span>
                      </div>
                      <span className="text-muted-foreground text-xs">
                        {getTypeLabel(item.type)}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
